import { Suspense } from 'react';
import { Outlet } from 'react-router';

import { useSession } from '../auth/SessionProvider';
import { UpdateBanner } from '../pwa/UpdateBanner';
import { useServiceWorkerUpdate } from '../pwa/useServiceWorkerUpdate';
import { LoadingScreen } from '../shell/LoadingScreen';
import { AppBar, AppBarProvider } from './AppBar';
import styles from './app.module.css';
import { TopNav } from './TopNav';

/** Frame shared by every screen: the update banner, the screen's app bar, the routed content,
 * and - once signed in - the four-item navigation. The bar and nav share one sticky chrome
 * block so the nav sits fixed directly under the title rather than the screen's bottom edge.
 * Each screen publishes its own bar content through `useAppBar`; sign-out lives on Ajustes,
 * reached from the nav. */
export function AppLayout() {
  const { status, owner } = useSession();
  const authenticated = status === 'authenticated' && owner !== null;
  // Registered once for the app's lifetime: `AppLayout` is the single root layout every route
  // renders under. An available update matters regardless of sign-in state, so the banner sits
  // above the bar rather than behind `authenticated`.
  const { needRefresh, applyUpdate } = useServiceWorkerUpdate();

  return (
    <AppBarProvider>
      {(bar) => (
        <div className={styles.app}>
          <UpdateBanner needRefresh={needRefresh} onUpdate={applyUpdate} />
          <div className={styles.chrome}>
            <AppBar content={bar} />
            {authenticated ? <TopNav /> : null}
          </div>
          <main className={styles.main}>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      )}
    </AppBarProvider>
  );
}
